import SparkMD5 from 'spark-md5';
import { measurePerformance } from '@/shared/lib/performance';
import { uploadWithRecovery, UploadError } from '@/features/angle';
import { getDeviceCapabilities } from '@/shared/lib/device';
import { normalizeHeicFile } from '@/shared/lib/heic';
import { createLogger } from '@/shared/lib/logger';

import type { UploadType } from '@/shared/constants';
import type { UploadPolicy } from '@/types/project-reference';

const logger = createLogger('ImageUploadService');

// --- Shared image sizing policy -------------------------------------------
// Single source of truth for how uploaded images are downscaled before S3.
// Both the upload service AND the crop canvas (useCropCanvas) consume these so
// they can never drift. Mirrors Android `ImageSizePolicy` and Apple
// `S3Uploader.ImageSizePolicy` — keep the three frontends in lockstep.

const PIXEL_MULTIPLE = 16;
const MAX_FILE_SIZE = 900 * 1024; // 900KB (references, controls)
const MAX_ANGLE_FILE_SIZE = 1500 * 1024; // 1.5MB (angles)
// Angle: ~2MP by AREA. A hard longest-side cap over-shoots on near-square
// photos (2048px ≈ 4.2MP square), so angles cap actual pixel area.
const ANGLE_TARGET_PIXELS = 2_000_000;
const CONTROL_TARGET_PIXELS = 1_000_000; // controls: ~1MP (PNG)
const MAX_REFERENCE_DIMENSION = 768; // references: 768px longest side

export interface ImageSizePolicy {
  /** `longSide` caps max(w,h); `area` caps w*h. Both only ever downscale. */
  mode: 'longSide' | 'area';
  target: number;
  /** JPEG byte budget the iterative quality ladder compresses down to. */
  maxFileBytes: number;
}

/**
 * Sizing policy per upload type:
 *  - angle: ~2MP by area (render pipeline), 1.5MB budget
 *  - reference: 768px longest side (classifier LLM only), 900KB budget
 *  - control (default): ~1MP by area (PNG), 900KB budget
 */
export function resolveImageSizePolicy(type?: UploadType): ImageSizePolicy {
  if (type === 'angle') {
    return { mode: 'area', target: ANGLE_TARGET_PIXELS, maxFileBytes: MAX_ANGLE_FILE_SIZE };
  }
  if (type === 'reference') {
    return { mode: 'longSide', target: MAX_REFERENCE_DIMENSION, maxFileBytes: MAX_FILE_SIZE };
  }
  return { mode: 'area', target: CONTROL_TARGET_PIXELS, maxFileBytes: MAX_FILE_SIZE };
}

/**
 * Downscaled, 16px-aligned target dimensions for the given source size and
 * policy. Only downscales (never upscales); aligns each side to 16px even when
 * no downscale is needed. Returns the same size when already at target.
 */
export function computeTargetDimensions(
  originalWidth: number,
  originalHeight: number,
  policy: Pick<ImageSizePolicy, 'mode' | 'target'>
): { width: number; height: number } {
  const align = (value: number) =>
    Math.max(PIXEL_MULTIPLE, Math.round(value / PIXEL_MULTIPLE) * PIXEL_MULTIPLE);

  if (originalWidth <= 0 || originalHeight <= 0) {
    return { width: PIXEL_MULTIPLE, height: PIXEL_MULTIPLE };
  }

  let scale = 1;
  if (policy.mode === 'longSide') {
    const longestSide = Math.max(originalWidth, originalHeight);
    if (longestSide > policy.target) scale = policy.target / longestSide;
  } else {
    const area = originalWidth * originalHeight;
    if (area > policy.target) scale = Math.sqrt(policy.target / area);
  }

  return {
    width: align(originalWidth * scale),
    height: align(originalHeight * scale),
  };
}
// --------------------------------------------------------------------------

export interface ImageUploadOptions {
  type: UploadType;
  maxFiles?: number;
  allowCrop?: boolean;
  onProgress?: (progress: UploadProgress) => void;
  onCleanupFailure?: (id: string) => Promise<void>;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface ProcessedFile {
  file: File;
  md5: string;
  width: number;
  height: number;
}

export interface UploadResult<TConfirmed = unknown> {
  id: string;
  url: string;
  type: UploadType;
  confirmed?: TConfirmed;
  /**
   * Where this file sat in the batch the caller handed in.
   *
   * A failed PUT drops its entry from the returned array, so position in the
   * result no longer answers "which file is this" — callers that read their own
   * previews and filenames by position showed each verdict under the previous
   * photo's thumbnail. Index by this, never by array position.
   */
  index: number;
}

export interface BatchUploadOptions extends ImageUploadOptions {
  mainAngleIndex?: number;
  /** Real S3 byte progress per file (index into the batch's file array). */
  onFileProgress?: (fileIndex: number, progress: UploadProgress) => void;
}

export type ConfirmCallback<TConfirmed> = (
  id: string
) => Promise<TConfirmed>;

export class ImageUploadService {
  // Cap on parallel presigned PUTs — 10 concurrent uploads saturate mobile
  // uplinks and starve each other's progress; 3 matches the retry budget UX.
  private static readonly MAX_CONCURRENT_UPLOADS = 3;
  private static readonly INITIAL_QUALITY = 0.85;
  private static readonly QUALITY_STEP = 0.1;
  private static readonly MAX_FILE_SIZE = 900 * 1024; // 900KB
  private static readonly MIN_QUALITY = 0.3;

  /**
   * Process and upload a single image file. If `confirm` is provided, it runs
   * after S3 success and must resolve before the upload is considered complete.
   * Failures at any stage (validate, create, S3, confirm) trigger
   * `onCleanupFailure` when the server record already exists.
   */
  async uploadSingle<TConfirmed = unknown>(
    file: File,
    options: ImageUploadOptions,
    apiCallback: (
      processedFile: ProcessedFile
    ) => Promise<{ id: string; uploadPolicy: UploadPolicy }>,
    confirm?: ConfirmCallback<TConfirmed>
  ): Promise<UploadResult<TConfirmed>> {
    const processedFile = await this.processImageFile(file, options);
    const { id, uploadPolicy } = await apiCallback(processedFile);

    try {
      // Server-side dedupe: when the API reports that an identical object
      // (matched by md5) already exists in R2, skip the S3 PUT and jump
      // straight to confirm. Removing this early-return would force a
      // redundant upload on every re-submission of the same file.
      if (!uploadPolicy.skip_upload) {
        await this.uploadToS3(
          processedFile.file,
          uploadPolicy,
          options.onProgress
        );
      } else if (options.onProgress) {
        options.onProgress({
          loaded: processedFile.file.size,
          total: processedFile.file.size,
          percentage: 100,
        });
      }

      const confirmed = confirm ? await confirm(id) : undefined;

      return {
        id,
        url: uploadPolicy.url,
        type: options.type,
        index: 0,
        ...(confirmed !== undefined ? { confirmed } : {}),
      } as UploadResult<TConfirmed>;
    } catch (error) {
      logger.debug('Upload failed after record creation, cleaning up:', error);
      await this.runCleanup(options, id);
      throw error;
    }
  }

  /**
   * Process and upload multiple image files. If `confirm` is provided, it runs
   * per-item after S3 success. Confirm failure is treated like an S3 failure
   * for that item (cleanup + excluded from the result).
   */
  async uploadBatch<TConfirmed = unknown>(
    files: File[],
    options: BatchUploadOptions,
    apiCallback: (
      processedFiles: ProcessedFile[],
      mainIndex?: number
    ) => Promise<Array<{ id: string; uploadPolicy: UploadPolicy }>>,
    confirm?: ConfirmCallback<TConfirmed>
  ): Promise<UploadResult<TConfirmed>[]> {
    const processedFiles = await Promise.all(
      files.map((file) => this.processImageFile(file, options))
    );

    const uploadData = await apiCallback(
      processedFiles,
      options.mainAngleIndex
    );

    const uploadOne = async (i: number) => {
      const processedFile = processedFiles[i];
      const data = uploadData[i];
      if (!processedFile || !data) {
        throw new Error(`No upload data for file ${i}`);
      }
      const { id, uploadPolicy } = data;

      try {
        // Server-side dedupe (batch): skip S3 PUT when the API reports an
        // existing object for this md5. See uploadSingle for the single-file
        // counterpart.
        if (!uploadPolicy.skip_upload) {
          await this.uploadToS3(processedFile.file, uploadPolicy, (progress) =>
            options.onFileProgress?.(i, progress)
          );
        } else {
          options.onFileProgress?.(i, {
            loaded: processedFile.file.size,
            total: processedFile.file.size,
            percentage: 100,
          });
        }

        const confirmed = confirm ? await confirm(id) : undefined;

        return {
          id,
          url: uploadPolicy.url,
          type: options.type,
          confirmed,
          index: i,
          success: true as const,
        };
      } catch (error) {
        console.error(`Failed to upload file ${i}:`, error);
        return {
          id,
          url: '',
          type: options.type,
          index: i,
          success: false as const,
          error,
        };
      }
    };

    // Bounded worker pool — at most MAX_CONCURRENT_UPLOADS PUTs in flight.
    const uploadResults: Array<Awaited<ReturnType<typeof uploadOne>>> =
      new Array(processedFiles.length);
    let nextIndex = 0;
    const workerCount = Math.min(
      ImageUploadService.MAX_CONCURRENT_UPLOADS,
      processedFiles.length
    );
    await Promise.all(
      Array.from({ length: workerCount }, async () => {
        for (;;) {
          const i = nextIndex++;
          if (i >= processedFiles.length) return;
          uploadResults[i] = await uploadOne(i);
        }
      })
    );

    const successfulResults = uploadResults.filter((r) => r.success);
    const failedIds = uploadResults.filter((r) => !r.success).map((r) => r.id);

    if (failedIds.length > 0) {
      logger.info(`Cleaning up ${failedIds.length} failed uploads`);
      await Promise.all(failedIds.map((id) => this.runCleanup(options, id)));
    }

    if (successfulResults.length === 0) {
      // Re-throw the first item's own failure so the reason survives to the
      // toast (network vs server, or a server-authored 413). A fresh generic
      // Error here would erase it and every locale would see the same
      // catch-all message.
      let firstFailure: unknown;
      for (const result of uploadResults) {
        if (!result.success) {
          firstFailure = result.error;
          break;
        }
      }
      throw firstFailure instanceof Error
        ? firstFailure
        : new UploadError('failed', 'All uploads failed');
    }

    return successfulResults.map((r) => ({
      id: r.id,
      url: r.url,
      type: r.type,
      index: r.index,
      ...(r.confirmed !== undefined ? { confirmed: r.confirmed } : {}),
    })) as UploadResult<TConfirmed>[];
  }

  private async runCleanup(
    options: ImageUploadOptions,
    id: string
  ): Promise<void> {
    if (!options.onCleanupFailure) return;
    try {
      await options.onCleanupFailure(id);
      logger.info('Cleaned up failed upload record:', id);
    } catch (err) {
      console.error('Failed to cleanup record after upload failure:', err);
    }
  }

  /**
   * Process a single file according to specifications
   * Handles both images and non-image files (PDFs, text files)
   */
  private async processImageFile(
    sourceFile: File,
    options?: ImageUploadOptions
  ): Promise<ProcessedFile> {
    return measurePerformance('processImageFile', async () => {
      // Safety net for callers that reach the service directly (support
      // attachments, controls) instead of through UnifiedImageUpload: a HEIC
      // no browser but Safari can decode would otherwise fail on the first
      // <img>. A no-op for everything else, including an already-transcoded
      // pick.
      const file = await normalizeHeicFile(sourceFile);

      // Check if file is a non-image type (PDF, text, etc.)
      const isNonImageFile = !file.type.startsWith('image/');

      if (isNonImageFile) {
        // For non-image files (PDFs, text files), skip image processing
        // Just calculate MD5 and return the file as-is
        logger.debug('Processing non-image file:', {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
        });

        const md5 = await this.calculateMD5(file);

        return {
          file,
          md5,
          width: 0,
          height: 0,
        };
      }

      // Get dimensions first to check if already at target size
      const dimensions = await this.getImageDimensions(file);

      // Check if file is already processed (correct dimensions and format).
      // "Already at target" ⇒ calculateTargetDimensions is a no-op (already
      // downscaled AND 16px-aligned). Anything else must be re-encoded so no
      // oversized/unaligned image reaches S3.
      const policy = this.resolveSizePolicy(options?.type);
      const target = this.calculateTargetDimensions(
        dimensions.width,
        dimensions.height,
        policy
      );
      const isCorrectSize =
        target.width === dimensions.width &&
        target.height === dimensions.height;
      // Controls must be PNG (transparency), other images - JPEG
      const acceptedFormats =
        options?.type === 'control' ? ['image/png'] : ['image/jpeg'];
      const isAlreadyProcessed =
        acceptedFormats.includes(file.type) &&
        file.size <= policy.maxFileBytes &&
        isCorrectSize;

      logger.debug('Processing file:', {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        dimensions: `${dimensions.width}x${dimensions.height}`,
        isCorrectSize,
        isAlreadyProcessed,
        uploadType: options?.type,
      });

      // If already processed (from crop), use it directly
      // Otherwise, convert and resize image based on type
      let processedFile: File;
      if (isAlreadyProcessed) {
        processedFile = file;
      } else if (options?.type === 'control') {
        // Controls must be PNG to preserve transparency
        processedFile = await this.resizePngImage(file);
      } else {
        // All other images (angles, references) are JPEG
        processedFile = await this.convertToOptimizedJpeg(file, options);
      }

      // Calculate MD5 hash of the final processed file
      const md5 = await this.calculateMD5(processedFile);

      // Get final dimensions if we processed the file
      const finalDimensions = isAlreadyProcessed
        ? dimensions
        : await this.getImageDimensions(processedFile);

      return {
        file: processedFile,
        md5,
        width: finalDimensions.width,
        height: finalDimensions.height,
      };
    });
  }

  private resolveSizePolicy(type?: UploadType): ImageSizePolicy {
    return resolveImageSizePolicy(type);
  }

  /**
   * Convert image to optimized JPEG (angles: 2048px, references: 768px longest
   * side; controls: 1MP)
   */
  private async convertToOptimizedJpeg(
    file: File,
    options?: ImageUploadOptions
  ): Promise<File> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = async () => {
        try {
          const policy = this.resolveSizePolicy(options?.type);
          const targetDimensions = this.calculateTargetDimensions(
            img.width,
            img.height,
            policy
          );

          // Create canvas
          const canvas = document.createElement('canvas');
          canvas.width = targetDimensions.width;
          canvas.height = targetDimensions.height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            throw new UploadError('prepare', 'Failed to get canvas context');
          }

          // Set high quality rendering
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';

          // Draw resized image
          ctx.drawImage(
            img,
            0,
            0,
            targetDimensions.width,
            targetDimensions.height
          );

          // Clean up
          URL.revokeObjectURL(url);

          // Convert to JPEG with quality optimization
          const optimizedFile = await this.optimizeJpegQuality(
            canvas,
            file.name,
            options
          );
          resolve(optimizedFile);
        } catch (error) {
          URL.revokeObjectURL(url);
          reject(error);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new UploadError('prepare', 'Failed to load image'));
      };

      img.src = url;
    });
  }

  /**
   * Downscaled, 16px-aligned target dimensions for the given source size and
   * policy. Only downscales (never upscales); aligns each side to 16px even
   * when no downscale is needed.
   *  - longSide: scale so the longest side <= target
   *  - area: scale so the pixel area ~= target
   */
  private calculateTargetDimensions(
    originalWidth: number,
    originalHeight: number,
    policy: Pick<ImageSizePolicy, 'mode' | 'target'>
  ): { width: number; height: number } {
    return computeTargetDimensions(originalWidth, originalHeight, policy);
  }

  /**
   * Optimize JPEG quality to ensure file size within limit
   */
  private async optimizeJpegQuality(
    canvas: HTMLCanvasElement,
    fileName: string,
    options?: ImageUploadOptions
  ): Promise<File> {
    const fileSizeLimit = this.resolveSizePolicy(options?.type).maxFileBytes;
    // Use maximum quality for control images to preserve black levels
    let quality =
      options?.type === 'control' ? 0.95 : ImageUploadService.INITIAL_QUALITY;
    let file: File | null = null;

    while (quality >= ImageUploadService.MIN_QUALITY) {
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', quality);
      });

      if (!blob) {
        throw new UploadError('prepare', 'Failed to convert canvas to blob');
      }

      file = new File([blob], fileName.replace(/\.[^/.]+$/, '.jpg'), {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      if (file.size <= fileSizeLimit) {
        break;
      }

      quality -= ImageUploadService.QUALITY_STEP;
    }

    if (!file) {
      throw new UploadError('prepare', 'Failed to optimize image quality');
    }

    return file;
  }

  /**
   * Calculate MD5 hash of file
   */
  private async calculateMD5(file: File): Promise<string> {
    return measurePerformance('calculateMD5', async () => {
      const capabilities = getDeviceCapabilities();
      const chunkSize = capabilities.chunkSize;
      const spark = new SparkMD5.ArrayBuffer();

      // For small files, calculate in one go
      if (file.size <= chunkSize) {
        const arrayBuffer = await file.arrayBuffer();
        spark.append(arrayBuffer);
        return spark.end();
      }

      // For larger files, calculate in chunks
      return new Promise((resolve, reject) => {
        const fileReader = new FileReader();
        let currentChunk = 0;
        const chunks = Math.ceil(file.size / chunkSize);

        const loadNext = () => {
          const start = currentChunk * chunkSize;
          const end = Math.min(start + chunkSize, file.size);
          fileReader.readAsArrayBuffer(file.slice(start, end));
        };

        fileReader.onload = (e) => {
          if (e.target?.result) {
            spark.append(e.target.result as ArrayBuffer);
            currentChunk++;

            if (currentChunk < chunks) {
              if ('requestIdleCallback' in window) {
                window.requestIdleCallback(() => loadNext());
              } else {
                setTimeout(loadNext, 0);
              }
            } else {
              resolve(spark.end());
            }
          }
        };

        fileReader.onerror = reject;
        loadNext();
      });
    });
  }

  /**
   * Upload file to S3/R2 via the presigned PUT URL provided by the server.
   */
  private async uploadToS3(
    file: File,
    uploadPolicy: UploadPolicy,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<void> {
    await uploadWithRecovery(
      file,
      uploadPolicy.url,
      {
        method: 'PUT',
        url: uploadPolicy.url,
        headers: uploadPolicy.headers,
      },
      onProgress
    );
  }

  /**
   * Resize PNG image while preserving transparency for control images
   * Note: PNG files may exceed MAX_FILE_SIZE due to transparency data
   */
  private async resizePngImage(file: File): Promise<File> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = async () => {
        try {
          // Controls: ~1MP by area, 16px-aligned (PNG path)
          const targetDimensions = this.calculateTargetDimensions(
            img.width,
            img.height,
            this.resolveSizePolicy('control')
          );

          // Create canvas
          const canvas = document.createElement('canvas');
          canvas.width = targetDimensions.width;
          canvas.height = targetDimensions.height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            throw new UploadError('prepare', 'Failed to get canvas context');
          }

          // Set high quality rendering
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';

          // Draw resized image on transparent canvas
          ctx.drawImage(
            img,
            0,
            0,
            targetDimensions.width,
            targetDimensions.height
          );

          // Clean up
          URL.revokeObjectURL(url);

          // Convert to PNG (lossless with transparency)
          const blob = await new Promise<Blob | null>((resolve) => {
            canvas.toBlob(resolve, 'image/png');
          });

          if (!blob) {
            throw new UploadError('prepare', 'Failed to convert canvas to blob');
          }

          const pngFile = new File(
            [blob],
            file.name.replace(/\.[^/.]+$/, '.png'),
            {
              type: 'image/png',
              lastModified: Date.now(),
            }
          );

          // Log warning if file size exceeds limit (but still allow it for controls)
          if (pngFile.size > ImageUploadService.MAX_FILE_SIZE) {
            logger.debug('PNG control exceeds MAX_FILE_SIZE:', {
              fileName: pngFile.name,
              fileSize: pngFile.size,
              maxSize: ImageUploadService.MAX_FILE_SIZE,
              note: 'PNG transparency may require larger file size',
            });
          }

          resolve(pngFile);
        } catch (error) {
          URL.revokeObjectURL(url);
          reject(error);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new UploadError('prepare', 'Failed to load image'));
      };

      img.src = url;
    });
  }

  /**
   * Get image dimensions
   */
  private async getImageDimensions(
    file: File
  ): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.width, height: img.height });
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new UploadError('prepare', 'Failed to load image'));
      };

      img.src = url;
    });
  }
}

// Export singleton instance
export const imageUploadService = new ImageUploadService();
